/**
 * Service for field-specific extraction API calls
 */
import { apiFetch } from '../utils/apiUtils';
import socketService from './socketService';

/**
 * Re-run field extraction for a specific field in a loan
 * @param {string} loanId - The loan ID
 * @param {string} fieldName - The field name to extract
 * @returns {Promise<Object>} The extraction results
 */
export const rerunFieldExtraction = async (loanId, fieldName) => {
  try {
    console.log(`🔄 Calling field extraction API for ${fieldName} in loan ${loanId}`);
    
    // Get current socket ID for targeted progress updates
    const socketId = socketService.getSocketId();
    console.log(`🔌 [Field Extraction] Using socket ID: ${socketId}`);
    
    const response = await apiFetch(`/extraction/rerun-field/${loanId}/${encodeURIComponent(fieldName)}?socket_id=${socketId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Include X-Test-Mode header in Cypress tests
        ...(window.Cypress ? { 'X-Test-Mode': 'true' } : {})
      }
    });
    
    // apiFetch already returns parsed JSON, so no need for response.ok or response.json()
    const result = response;
    
    if (!result.success) {
      throw new Error(result.error || 'Field extraction failed');
    }
    
    console.log(`✅ Field extraction API call successful for ${fieldName}:`, result);
    return result;
    
  } catch (error) {
    console.error(`❌ Field extraction API call failed for ${fieldName}:`, error);
    throw error;
  }
};

/**
 * Mock field extraction for development/testing
 * @param {string} loanId - The loan ID
 * @param {string} fieldName - The field name to extract
 * @returns {Promise<Object>} Mock extraction results
 */
export const mockRerunFieldExtraction = async (loanId, fieldName) => {
  console.log(`🧪 Using mock field extraction for ${fieldName} in loan ${loanId}`);
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Return mock data based on field name
  const mockResults = {
    success: true,
    field_name: fieldName,
    core_value: 'Mock Core Value',
    document_results: [
      {
        document: 'note',
        value: 'Mock Note Value',
        confidence: 0.95,
        page: 1,
        found: true,
        source: 'note_2024_01_15'
      },
      {
        document: 'proposal',
        value: 'Mock Proposal Value',
        confidence: 0.88,
        page: 2,
        found: true,
        source: 'proposal_2024_01_15'
      },
      {
        document: 'service_agreement',
        value: 'Not Found',
        confidence: 0.0,
        page: null,
        found: false,
        source: null
      }
    ]
  };
  
  console.log(`✅ Mock field extraction completed for ${fieldName}:`, mockResults);
  return mockResults;
};
